import React, { useState } from 'react';
import { addDays } from 'date-fns';
import { Info } from 'lucide-react';
import { addPeriod, topla } from './periodMath';
import { kalanSure, sureIfade, durmaToplamIfade } from './formatlayicilar';
import './SurecHaritasi.css';

/* =====================================================================
   ZAMANAŞIMI SÜREÇ HARİTASI — §01 zaman çizelgesi
   "zamanasimi-surec-haritasi.html" belgesindeki NLE şema-timeline tasarımı
   birebir taşındı. Çizim mantığı belgeyle aynıdır; yalnızca veri kaynağı
   değişti: tüm tarihler motorun (zamanAsimiHesaplayici) ürettiği gerçek
   (hukuken bağlayıcı) tarihlerden gelir — burada yeniden hesap yapılmaz.
   ===================================================================== */

const dot = (d) =>
  `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
const days = (a, b) => Math.round((b - a) / 86400000);

function ymd(a, b) {
  if (b < a) {
    const t = a;
    a = b;
    b = t;
  }
  let y = b.getFullYear() - a.getFullYear();
  let m = b.getMonth() - a.getMonth();
  let d = b.getDate() - a.getDate();
  if (d < 0) {
    m--;
    d += new Date(b.getFullYear(), b.getMonth(), 0).getDate();
  }
  if (m < 0) {
    y--;
    m += 12;
  }
  return { y, m, d, total: days(a, b) };
}
function durStr(o) {
  const p = [];
  if (o.y) p.push(o.y + ' yıl');
  if (o.m) p.push(o.m + ' ay');
  if (o.d) p.push(o.d + ' gün');
  return p.length ? p.join(' ') : '0 gün';
}
function durShort(o) {
  const p = [];
  if (o.y) p.push(o.y + 'y');
  if (o.m) p.push(o.m + 'a');
  if (o.d) p.push(o.d + 'g');
  return p.length ? p.join(' ') : '0g';
}
/** Süre (period) etiketi: {year,month,day} → "8 yıl" / "5 yıl 4 ay". */
function sureLabel(p) {
  if (!p) return '';
  const out = [];
  if (p.year) out.push(p.year + ' yıl');
  if (p.month) out.push(p.month + ' ay');
  if (p.day) out.push(p.day + ' gün');
  return out.join(' ') || '0 gün';
}

/* ---- saat-eylem işaretleri (play/pause/reset) olay bileşenleri üstünde ---- */
const ICON = {
  play: '<svg viewBox="0 0 12 12" fill="currentColor"><path d="M3.4 2.4 9.4 6 3.4 9.6Z"/></svg>',
  pause:
    '<svg viewBox="0 0 12 12" fill="currentColor"><rect x="3" y="2.4" width="2.1" height="7.2" rx=".5"/><rect x="6.9" y="2.4" width="2.1" height="7.2" rx=".5"/></svg>',
  stop: '<svg viewBox="0 0 12 12" fill="currentColor"><rect x="2.7" y="2.7" width="6.6" height="6.6" rx="1.2"/></svg>',
};
const ACT = {
  start: { icons: ['play'], col: 'var(--nle-start)', lab: 'süreyi başlatır' },
  reset: { icons: ['stop', 'play'], col: 'var(--nle-kesme)', lab: 'süreyi keser ve yeniden başlatır' },
  pause: { icons: ['pause'], col: 'var(--nle-amber)', lab: 'süreyi durdurur' },
  resume: { icons: ['pause', 'play'], col: 'var(--nle-amber)', lab: 'durma sona erer — süre yeniden işler' },
};
function actMark(act) {
  const a = ACT[act];
  if (!a) return { top: '', bot: '' };
  const glyphs = a.icons.map((n) => ICON[n]).join('');
  const cls = 'smark' + (a.icons.length > 1 ? ' multi' : '');
  const badge = (pos) => `<span class="${cls} ${pos}" title="${a.lab}" style="color:${a.col}">${glyphs}</span>`;
  return { top: badge('top'), bot: badge('bot') };
}

/* ---------------------------------------------------------------------
   ADAPTÖR: motor (timelineModel + sonuc) → çizicinin beklediği "c" modeli.
   Tüm tarihler motordan; süre etiketleri ve "durmasız" projeksiyon uçları
   yaş indirimine saygılı period aritmetiğiyle (periodMath) türetilir.
   --------------------------------------------------------------------- */
function buildModel(timelineModel, sonuc) {
  const tm = timelineModel;
  const suc = new Date(tm.sucTarihi);
  const bugun = new Date(tm.bugun);
  const olaganZA = new Date(tm.ordinaryDeadline);
  const olaganustuZA = new Date(tm.ceiling.end);
  const earlier = new Date(tm.effective);
  const winner = tm.capApplied ? 'olaganustu' : 'olagan';
  const doldu = tm.zamanAsimiDoldu;

  // Etkili kesmeler (saati sıfırlayanlar) — runs ile 1:1 hizalı, kronolojik
  const effKesmeler = (tm.kesmeler || [])
    .filter((k) => k.effective)
    .map((k) => ({ ...k, d: new Date(k.date) }))
    .sort((a, b) => a.d - b.d);
  const kesmeler = effKesmeler.map((k) => ({ d: k.d, tip: k.type || 'Kesme' }));
  // Etkisiz kesmeler — veriyi kaybetmemek için üst eksende sönük iğne olarak
  const kesmelerEtkisiz = (tm.kesmeler || [])
    .filter((k) => !k.effective)
    .map((k) => ({ d: new Date(k.date), tip: k.type || 'Kesme' }));

  const durmalar = (tm.durmalar || [])
    .map((dd) => ({ bas: new Date(dd.start), bit: new Date(dd.end), gun: dd.gun, sebep: dd.label || dd.type || 'Durma' }))
    .sort((a, b) => a.bas - b.bas);

  // Koşular (fan kaynakları) — her run motorun verdiği projeksiyon/operatif uç ile
  const runs = (tm.runs || []).map((r) => ({
    start: new Date(r.start),
    end: new Date(r.end),
    supersededAt: r.supersededAt ? new Date(r.supersededAt) : null,
    isActive: !!r.isActive,
    durmaGun: r.durmaGun || 0,
    index: r.index,
  }));

  const hasKesme = kesmeler.length > 0;
  const olaganStart = runs.length ? runs[runs.length - 1].start : suc; // son kesme (yoksa suç)
  const olaganDurmalar = durmalar.filter((d) => d.bas >= olaganStart);
  const allDurmaGun = durmalar.reduce((s, d) => s + d.gun, 0);

  // Süre etiketleri + "durmasız" projeksiyon uçları (period aritmetiği)
  const yas = sonuc && sonuc.yasGrubuBilgisi;
  const ayarlanmis = yas ? yas.ayarlanmisSure : null;
  const yari = yas ? yas.kanuniSureninYarisi : null;
  const ustSinir = ayarlanmis && yari ? topla(ayarlanmis, yari) : null;
  const olaganLabel = sureLabel(ayarlanmis);
  const olaganustuLabel = sureLabel(ustSinir);
  const olaganCleanEnd = ayarlanmis ? addPeriod(olaganStart, ayarlanmis) : null;
  const olaganustuCleanEnd = ustSinir ? addPeriod(suc, ustSinir) : null;

  return {
    suc,
    bugun,
    olaganZA,
    olaganustuZA,
    earlier,
    winner,
    doldu,
    kesmeler,
    kesmelerEtkisiz,
    durmalar,
    runs,
    hasKesme,
    olaganStart,
    olaganDurmalar,
    allDurmaGun,
    olaganLabel,
    olaganustuLabel,
    olaganCleanEnd,
    olaganustuCleanEnd,
  };
}

/* ---------------------------------------------------------------------
   ÇİZİCİ: belgedeki buildTimeline mantığının birebir taşınması.
   `.nle-inner` iç HTML'i + durma özeti HTML'i döndürür.
   --------------------------------------------------------------------- */
function buildTimelineHTML(c) {
  const { suc, bugun, olaganZA, olaganustuZA, winner, doldu } = c;

  // ---- şematik, oransız eksen: tüm önemli tarihlerde eşit-aralıklı kolonlar ----
  // Eskiden sağ uçta tek bir ORANTILI bölge (ZONE) vardı: son olay ile deadline'lar
  // arasındaki boşluk gerçek zamanla ölçeklendiğinden senaryoya göre çok değişken
  // (tutarsız) bir boşluk ve aşırı uzayan tek bir segment üretiyordu. Artık deadline
  // tarihleri de birer kolon olduğundan ritim tüm çizelge boyunca tek-tiptir; segment
  // genişlikleri orantı yerine kolon ızgarasına oturur → düzenli, profesyonel görünüm.
  const HEAD = 62;
  const STEP = 132; // olaylar/deadline'lar arası tek-tip kolon genişliği
  const DURW = 210; // durma kolonu: başlangıç+bitiş head'leri + taralı bandı barındırır
  const LEFT = 16;
  const RPAD = 140; // son kolondaki sonuç bileşeni + üst rozet/etiketler için sağ pay
  const GAP = 5; // bilgi bloğu (head/sonuç) ↔ zaman bloğu (segment/durma bandı) arası tek-tip minimal boşluk

  // Üst-eksen rozetleri, dropline'lar ve meta YALNIZ gerçek olay tarihlerinden beslenir.
  const anchors = [
    ...new Set([
      suc.getTime(),
      ...c.kesmeler.map((k) => k.d.getTime()),
      ...c.durmalar.flatMap((d) => [d.bas.getTime(), d.bit.getTime()]),
    ]),
  ].sort((a, b) => a - b);

  // X geometrisi: olay tarihleri + deadline tarihleri birlikte tek-tip kolon ızgarası.
  // (bugün kolon DEĞİL; ince todayline olarak kolonlar arasında interpole edilir.)
  const cols = [...new Set([...anchors, olaganZA.getTime(), olaganustuZA.getTime()])].sort((a, b) => a - b);
  const m = cols.length;
  const durSet = new Set(c.durmalar.map((d) => d.bas.getTime() + '_' + d.bit.getTime()));
  const gapW = [];
  for (let i = 0; i < m - 1; i++) {
    gapW.push(durSet.has(cols[i] + '_' + cols[i + 1]) ? DURW : STEP);
  }
  const xpos = [LEFT];
  for (let i = 0; i < m - 1; i++) xpos.push(xpos[i] + gapW[i]);
  const PLOTW = xpos[m - 1] + HEAD + RPAD;
  function AX(d) {
    const t = d.getTime ? d.getTime() : d;
    if (t <= cols[0]) return LEFT;
    for (let i = 0; i < m - 1; i++) {
      if (t <= cols[i + 1]) return xpos[i] + (gapW[i] * (t - cols[i])) / (cols[i + 1] - cols[i]);
    }
    return xpos[m - 1]; // son kolonun ötesi (nadir) → son kolona sabitle
  }

  // ---- üst eksen: yalnız önemli tarihler ----
  const meta = {};
  meta[suc.getTime()] = { t: 'Suç', d: suc };
  c.kesmeler.forEach((k) => {
    meta[k.d.getTime()] = { t: 'Kesme', d: k.d };
  });
  c.durmalar.forEach((dd) => {
    meta[dd.bas.getTime()] = { t: 'Durma', d: dd.bas };
    meta[dd.bit.getTime()] = { t: 'Bitiş', d: dd.bit };
  });
  function markX(d) {
    const t = d.getTime ? d.getTime() : d;
    const mm = meta[t];
    return mm ? (mm.t === 'Bitiş' ? AX(t) - HEAD / 2 : AX(t) + HEAD / 2) : AX(t);
  }
  // dropline'lar, üstteki TÜM tarih rozetleri (marks) hesaplandıktan sonra kurulur (aşağıda);
  // böylece yalnız olay tarihleri değil, şerit/deadline tarihleri de aşağıya bağlanır.
  let dropHTML = '';
  const roleAc = (t) =>
    t === 'Suç'
      ? 'var(--nle-start)'
      : t === 'Kesme'
      ? 'var(--nle-kesme)'
      : t === 'Durma' || t === 'Bitiş'
      ? 'var(--nle-amber)'
      : 'var(--nle-faint)';
  const ribDates = [];
  function resolveTier(items) {
    const MINGAP = 32;
    let prev = -Infinity;
    items.forEach((it) => {
      it.bx = Math.max(it.x, prev + MINGAP);
      prev = it.bx;
    });
    const RIGHT = PLOTW - 22;
    let over = items.length ? Math.max(0, items[items.length - 1].bx - RIGHT) : 0;
    if (over > 0)
      for (let i = items.length - 1; i >= 0; i--) {
        items[i].bx -= over;
        if (i > 0 && items[i].bx - items[i - 1].bx >= MINGAP) break;
      }
  }
  function topBadge(it) {
    const by = 14;
    const cls = 'tdate' + (it.now ? ' now' : '') + (it.kind === 'rib' ? ' rib' : '');
    const cap = it.kind === 'rib' ? '' : `<span class="tt">${it.tt}</span>`;
    const dtColor = it.kind === 'rib' ? `color:${it.ac}` : '';
    const colorVars = it.kind === 'rib' ? `--badge-ac:${it.ac}` : `--badge-ac:${it.now ? '#fff' : it.ac}`;
    return (
      `<div class="${cls}" style="left:${it.x.toFixed(1)}px;--bx:${(it.bx - it.x).toFixed(1)}px;--by:${by}px;${colorVars}">` +
      `<span class="tlead" style="height:${by - 9}px"></span><span class="tdot"></span>` +
      `<div class="tbadge">${cap}<span class="tdt" style="${dtColor}">${dot(it.date)}</span></div></div>`
    );
  }

  // ---- işleyen dönem YALNIZ durma (pause) ile bölünür; kesme bölmez ----
  function segsFor(start, end, durmaList) {
    const s = start.getTime();
    const e = end.getTime();
    const ds = (durmaList || c.durmalar)
      .map((d) => ({ a: d.bas.getTime(), b: d.bit.getTime(), gun: d.gun, sebep: d.sebep }))
      .filter((d) => d.b > s && d.a < e)
      .sort((x, y) => x.a - y.a);
    const out = [];
    let cur = s;
    ds.forEach((d) => {
      const a = Math.max(d.a, s);
      const b = Math.min(d.b, e);
      if (a > cur) out.push({ a: new Date(cur), b: new Date(a), pause: false, gun: 0 });
      out.push({ a: new Date(a), b: new Date(b), pause: true, gun: d.gun, sebep: d.sebep });
      cur = b;
    });
    if (cur < e) out.push({ a: new Date(cur), b: new Date(e), pause: false, gun: 0 });
    return out;
  }

  // ---- head = dikey 3-kolon bileşen (tür · tarih · olay) + saat-eylem işaretleri ----
  function headBox(left, head) {
    const ev = head.event && head.event.length > 13 ? head.event.slice(0, 12) + '…' : head.event || '';
    const mk = head.act ? actMark(head.act) : { top: '', bot: '' };
    return `<div class="shead" style="left:${left.toFixed(1)}px;width:${HEAD}px;border-bottom-color:${head.color}">${mk.top}<div class="c1">${head.type}</div><div class="c2">${head.date}</div><div class="c3">${ev}</div>${mk.bot}</div>`;
  }

  // ---- sonuç bileşeni: şerit sonunda 3-kolon kart (tür · tarih · sonuç) ----
  function endComp(x, type, date, note, accent, bg, badge) {
    const b = badge
      ? `<span class="ebadge" style="background:${badge.bg};color:${badge.fg}">${badge.txt}</span>`
      : '';
    return `<div class="shead send" style="left:${x.toFixed(1)}px;width:${HEAD}px;background:${bg};border-color:${accent};border-bottom-color:${accent}">${b}<div class="c1" style="color:${accent}">${type}</div><div class="c2">${date}</div><div class="c3" style="color:${accent}">${note}</div></div>`;
  }

  // ---- segmentler + durma; ilk işleyen segment firstOffset'ten başlar (head'i geçer) ----
  function segsHTML(start, end, sup, firstOffset, runColor, runBg) {
    let h = '';
    segsFor(start, end).forEach((s, idx) => {
      if (s.pause) {
        const bx = AX(s.a);
        const ex2 = AX(s.b);
        // bant, başlangıç/bitiş head'lerinden iki yana da GAP kadar içeride durur
        const bandL = bx + HEAD + GAP;
        const bandW = ex2 - HEAD - GAP - bandL;
        if (bandW > 3) {
          const lab = durShort(ymd(s.a, s.b)); // kısa biçim: "3y 1a 5g" (segmentlerle tutarlı)
          h += `<div class="spause" style="left:${bandL.toFixed(1)}px;width:${bandW.toFixed(1)}px"><span class="riblab">${lab}</span></div>`;
        }
        h += headBox(bx, { type: 'Durma', date: dot(s.a), event: s.sebep || '', color: 'var(--nle-amber)', act: 'pause' });
        h += headBox(ex2 - HEAD, { type: 'Durma sonu', date: dot(s.b), event: '', color: 'var(--nle-amber)', act: 'resume' });
      } else {
        // segment, önündeki ve ardındaki bilgi bloğundan GAP kadar boşluk bırakır.
        // (idx 0 → firstOffset = strip başı head'i + HEAD + GAP; sonrakiler durma
        // bitişi head'inin sağ kenarından + GAP başlar.) Sağda her zaman -GAP.
        const segL = idx === 0 ? firstOffset : AX(s.a) + GAP;
        const w = AX(s.b) - GAP - segL;
        if (w <= 3) return;
        const lab = w > 30 ? durShort(ymd(s.a, s.b)) : '';
        h += `<div class="sseg ${sup ? 'sup' : ''}" style="left:${segL.toFixed(1)}px;width:${w.toFixed(1)}px;${
          sup ? '' : 'background:' + runBg + ';'
        }color:${runColor}">${lab}</div>`;
      }
    });
    return h;
  }

  // ---- bir sürekli ince şerit: köken → potansiyel uç, durma-duyarlı ----
  function ribbonRow(origin, potEnd, fg, bg, nm, sub, durmaList, alt, cls, runLabel) {
    const x0 = markX(origin);
    const x1 = markX(potEnd);
    let bars = `<div class="ribtrack" style="left:${x0.toFixed(1)}px;width:${Math.max(x1 - x0, 2).toFixed(
      1
    )}px;background:${fg}"></div>`;
    let durTot = 0;
    segsFor(origin, potEnd, durmaList).forEach((s) => {
      const L = markX(s.a);
      const W = markX(s.b) - L;
      if (s.pause) {
        durTot += s.gun;
        if (W > 3) {
          const sh = durShort(ymd(s.a, s.b)); // kısa biçim: "3y 1a 5g"
          const lab = W > 16 ? sh : '';
          bars += `<div class="ribpause" title="durma · ${durStr(ymd(s.a, s.b))}" style="left:${L.toFixed(1)}px;width:${W.toFixed(
            1
          )}px">${lab ? `<span class="riblab">${lab}</span>` : ''}</div>`;
        }
      } else {
        const lab = W > 40 ? durShort(ymd(s.a, s.b)) : '';
        bars += `<div class="ribrun" title="${durStr(ymd(s.a, s.b))}" style="left:${L.toFixed(1)}px;width:${Math.max(
          W,
          2
        ).toFixed(1)}px;background:${bg};color:${fg};border:1px ${alt ? 'dashed' : 'solid'} ${fg}">${
          lab ? `<span class="riblab">${lab}</span>` : ''
        }</div>`;
      }
    });
    bars += `<div class="ribdot" style="left:${x0.toFixed(1)}px;background:${fg}"></div>`;
    bars += `<div class="ribdot" style="left:${x1.toFixed(1)}px;background:${fg}"></div>`;
    if (durTot > 0 && runLabel)
      bars += `<div class="ribsum" style="left:${x1.toFixed(1)}px"><span class="riblab">işleyen <b>${runLabel}</b></span></div>`;
    ribDates.push({ x: x1, date: potEnd, fg });
    return (
      `<div class="srow ribbon${alt ? ' alt' : ''}${cls ? ' ' + cls : ''}${potEnd <= bugun ? ' past' : ''}"><div class="srlab">` +
      `<span class="nm"><span class="swatch" style="background:${fg}"></span>${nm}</span>` +
      `<span class="sub">${sub}</span></div>` +
      `<div class="splot" style="width:${PLOTW}px">${bars}</div></div>`
    );
  }

  // ---- durma alternatifleri: "durmasız" + her durma kümülatif eklendikçe kademeli şeritler ----
  const DA = ['var(--nle-da1)', 'var(--nle-da2)', 'var(--nle-da3)'];
  function durmaStairs(origin, cleanEnd, durList, runLabel) {
    if (!durList.length || !cleanEnd) return '';
    let html = '';
    let cum = 0;
    for (let k = 0; k < durList.length; k++) {
      const endK = addDays(cleanEnd, cum);
      const fg = DA[k % DA.length];
      const nm = k === 0 ? '⏳ durmasız' : '⏳ ' + k + '. durmaya kadar';
      const sub = k === 0 ? 'durma çıkarılmış' : k + ' durma uygulanmış';
      html += ribbonRow(origin, endK, fg, 'var(--nle-da-bg)', nm, sub, durList.slice(0, k), true, '', runLabel);
      cum += durList[k].gun;
    }
    return html;
  }

  // ---- bir şerit: head (başta) + segmentler + sonda bayrak/sonuç bileşeni ----
  function renderStrip(label, sub, head, start, end, runColor, runBg, flag, sup, extraHTML) {
    const x0 = AX(start);
    let h = `<div class="srow"><div class="srlab"><span class="nm"><span class="swatch" style="background:${runColor}"></span>${label}</span>${
      sub ? `<span class="sub">${sub}</span>` : ''
    }</div><div class="splot" style="width:${PLOTW}px">`;
    h += headBox(x0, head);
    h += segsHTML(start, end, sup, x0 + HEAD + GAP, runColor, runBg);
    const ex = AX(end).toFixed(1);
    if (flag.sup) {
      h += `<div class="sflag sup2" style="left:${ex}px"><span class="fdot" style="background:${runColor};opacity:.55"></span>— sıfırlandı</div>`;
    } else {
      h += endComp(AX(end), flag.type, dot(end), flag.note, flag.accent, flag.bg, flag.badge);
    }
    return h + (extraHTML || '') + `</div></div>`;
  }

  // ---- iki saat, iki alan (zone) ----
  const resCol = doldu ? 'var(--nle-red)' : 'var(--nle-green)';
  const resBg = doldu ? 'var(--nle-dead-bg)' : 'var(--nle-ok-bg)';
  const tagBg = doldu ? 'var(--red-bg)' : 'var(--green-bg)';
  const tagFg = doldu ? 'var(--red)' : 'var(--green)';
  const olWin = winner === 'olagan';
  function ribColor(potEnd, idHue, idRib) {
    if (+potEnd === +c.earlier) return { fg: resCol, bg: resBg };
    if (potEnd <= bugun) return { fg: 'var(--nle-past)', bg: 'var(--nle-past-bg)' };
    return { fg: idHue, bg: idRib };
  }

  // === OLAĞANÜSTÜ alanı ===
  let su = '';
  su += renderStrip(
    'Olağanüstü Z.A.',
    '67/4 tavan',
    { type: 'Suç tarihi', date: dot(suc), event: 'tavan başladı', color: 'var(--nle-ou)', act: 'start' },
    suc,
    olaganustuZA,
    'var(--nle-ou)',
    'var(--nle-ou-bg)',
    olWin
      ? { type: 'Olağanüstü Z.A.', note: '67/4 tavan', accent: 'var(--nle-ou)', bg: 'var(--nle-ou-end)' }
      : {
          type: 'Olağanüstü Z.A.',
          note: 'dava düşer',
          accent: resCol,
          bg: resBg,
          badge: { txt: 'önce dolan', bg: tagBg, fg: tagFg },
        },
    false
  );
  {
    const rc = ribColor(olaganustuZA, 'var(--nle-ou)', 'var(--nle-ou-rib)');
    su += ribbonRow(
      suc,
      olaganustuZA,
      rc.fg,
      rc.bg,
      '⏳ Suç’tan',
      '+' + c.olaganustuLabel + ' · tavan',
      undefined,
      false,
      'op',
      c.olaganustuLabel
    );
  }
  su += durmaStairs(suc, c.olaganustuCleanEnd, c.durmalar, c.olaganustuLabel);

  // === OLAĞAN alanı ===
  const hasK = c.hasKesme;
  const olc = 'var(--nle-ol)';
  const olbg = 'var(--nle-ol-bg)';
  const opStart = c.olaganStart;
  let so = '';
  {
    const sub = hasK ? 'son kesmeden işleyen süre' : 'işleyen süre';
    let h = `<div class="srow"><div class="srlab"><span class="nm"><span class="swatch" style="background:${olc}"></span>Olağan Z.A.</span><span class="sub">${sub}</span></div><div class="splot" style="width:${PLOTW}px">`;
    h += headBox(AX(suc), { type: 'Suç tarihi', date: dot(suc), event: 'suç işlendi', color: olc, act: 'start' });
    if (hasK) {
      // Üzeri çizilen (superseded) kısmı TEK birleşik segment yerine her koşuyu KENDİ aralığında
      // çiz (suç→kesme1, kesme1→kesme2 …). Tek segmentte tek toplam etiket ortaya gelip aradaki
      // kesme kutusunun (z-index üstte) arkasında kalıyordu; aralık aralık çizilince her sürenin
      // etiketi iki kutu arasındaki boşlukta görünür.
      c.runs
        .filter((r) => !r.isActive && r.supersededAt)
        .forEach((r) => {
          h += segsHTML(r.start, r.supersededAt, true, AX(r.start) + HEAD + GAP, olc, olbg);
        });
      c.kesmeler.forEach((k) => {
        h += headBox(AX(k.d), { type: 'Kesme', date: dot(k.d), event: k.tip, color: olc, act: 'reset' });
      });
      h += segsHTML(opStart, olaganZA, false, AX(opStart) + HEAD + GAP, olc, olbg);
    } else {
      h += segsHTML(suc, olaganZA, false, AX(suc) + HEAD + GAP, olc, olbg);
    }
    if (olWin) {
      h += endComp(AX(olaganZA), 'Olağan Z.A.', dot(olaganZA), 'dava düşer', resCol, resBg, {
        txt: 'önce dolan',
        bg: tagBg,
        fg: tagFg,
      });
    } else {
      h += endComp(AX(olaganZA), 'Olağan Z.A.', dot(olaganZA), 'süre sonu', olc, 'var(--nle-ol-end)');
    }
    so += h + `</div></div>`;
  }
  // olağan potansiyel şeritler (fan): her koşu → sonuncusu OPERATİF, öncekiler SIFIRLANAN projeksiyon
  c.runs.forEach((r, i) => {
    const isOp = r.isActive;
    const nm = i === 0 ? '⏳ Suç’tan' : '⏳ ' + i + '. kesme’den';
    const rc = ribColor(r.end, 'var(--nle-ol)', 'var(--nle-ol-rib)');
    const sub = isOp ? '+' + c.olaganLabel + ' · etkin' : 'kesmeyle sıfırlandı';
    so += ribbonRow(r.start, r.end, rc.fg, rc.bg, nm, sub, undefined, false, isOp ? 'op' : 'reset', c.olaganLabel);
  });
  so += durmaStairs(opStart, c.olaganCleanEnd, c.olaganDurmalar, c.olaganLabel);

  // ---- üstteki tarih rozetleri: olaylar + şerit deadline'ları tek sıra, çakışma çözülür ----
  const marks = anchors.map((t) => ({ x: markX(t), date: meta[t].d, tt: meta[t].t, ac: roleAc(meta[t].t), kind: 'ev' }));
  marks.push({ x: AX(bugun), date: bugun, tt: 'Bugün', now: true, kind: 'ev' });
  // etkisiz kesmeler — sönük iğne (reset değil; baş bileşeni/şeridi yok)
  c.kesmelerEtkisiz.forEach((k) => {
    if (!meta[k.d.getTime()]) marks.push({ x: AX(k.d), date: k.d, tt: 'Kesme', ac: 'var(--nle-faint)', kind: 'ev' });
  });
  const ribMap = new Map();
  ribDates.forEach((r) => {
    const k = dot(r.date);
    if (!ribMap.has(k) || r.fg === resCol) ribMap.set(k, { x: r.x, date: r.date, ac: r.fg, kind: 'rib' });
  });
  ribMap.forEach((v, k) => {
    if (marks.some((mk) => dot(mk.date) === k)) ribMap.delete(k);
  });
  marks.push(...ribMap.values());
  marks.sort((a, b) => a.x - b.x);
  resolveTier(marks);
  // Üstteki HER tarih rozeti için bir dropline (bugün hariç — onun tam-boy todayline'ı var).
  dropHTML = marks
    .filter((mk) => !mk.now)
    .map((mk) => `<div class="dropline" style="left:${mk.x.toFixed(1)}px"></div>`)
    .join('');
  const axHTML = marks.map(topBadge).join('');

  const zone = (mod, inner) => `<div class="szone szone--${mod}">${inner}</div>`;
  const nleHTML =
    `<div class="topax"><div class="axsp"></div><div class="axcv" style="width:${PLOTW}px">${axHTML}</div></div>` +
    `<div class="strips"><div class="dropoverlay" style="width:${PLOTW}px">${dropHTML}</div>` +
    zone('olg', so) +
    `<div class="szspacer"></div>` +
    zone('ust', su) +
    `<div class="todayline" style="left:calc(var(--clab) + ${AX(bugun).toFixed(1)}px)"></div></div>`;

  // ---- durma özeti ----
  let durSummaryHTML;
  if (c.durmalar.length) {
    const rows = c.durmalar
      .map(
        (dd) =>
          `<div class="row"><span class="pdot"></span><span><b class="mono">${dot(dd.bas)} – ${dot(
            dd.bit
          )}</b> · +${sureIfade(dd.bas, dd.bit, dd.gun).ifade} · ${dd.sebep}</span></div>`
      )
      .join('');
    durSummaryHTML =
      rows +
      `<div class="row"><span class="pdot" style="background:transparent"></span><span class="tot">Toplam +${durmaToplamIfade(
        c.durmalar,
        c.allDurmaGun
      )} — durma, zamanaşımı süresini durdurur; şeritlerde başlangıç/bitiş bileşenleriyle çerçevelenir ve her iki tarihi ileri kaydırır.</span></div>`;
  } else {
    durSummaryHTML = `<div class="row"><span class="none">Bu senaryoda durma yok — zamanaşımı süresi hiç durmadı.</span></div>`;
  }

  return { nleHTML, durSummaryHTML };
}

const LEGEND_HTML = `
  <span><b style="color:var(--ink);font-weight:600">İki zamanaşımı:</b></span>
  <span><span class="swatch" style="background:#5aa0e6"></span>olağan Z.A. (mavi)</span>
  <span><span class="swatch" style="background:#9690c9"></span>olağanüstü Z.A. — 67/4 tavan (indigo)</span>
  <span><b style="color:var(--ink);font-weight:600">Şerit durumu:</b></span>
  <span><span class="swatch" style="background:#5aa0e6"></span>etkin / ileriye dönük öngörü</span>
  <span><span class="swatch" style="background:transparent; border:1px dashed var(--ink-faint)"></span>kesmeyle sıfırlanan (soluk)</span>
  <span><span class="swatch" style="background:var(--slate)"></span>geçmiş (dolmuş)</span>
  <span><span class="swatch" style="background:#2c171a; border:1px solid var(--red)"></span>önce dolan — sonuç (kırmızı/yeşil)</span>
  <span><b style="color:var(--ink);font-weight:600">Olay işaretleri:</b></span>
  <span>
    <svg width="11" height="11" viewBox="0 0 12 12" fill="#3fd6c8" style="vertical-align:-1px"><path d="M3.4 2.4 9.4 6 3.4 9.6Z"/></svg> başlat ·
    <svg width="11" height="11" viewBox="0 0 12 12" fill="#6fb0ee" style="vertical-align:-1px"><rect x="2.7" y="2.7" width="6.6" height="6.6" rx="1.2"/></svg><svg width="11" height="11" viewBox="0 0 12 12" fill="#6fb0ee" style="vertical-align:-1px"><path d="M3.4 2.4 9.4 6 3.4 9.6Z"/></svg> kesme ·
    <svg width="11" height="11" viewBox="0 0 12 12" fill="#e0a33c" style="vertical-align:-1px"><rect x="3" y="2.4" width="2.1" height="7.2" rx=".5"/><rect x="6.9" y="2.4" width="2.1" height="7.2" rx=".5"/></svg> durma ·
    <svg width="11" height="11" viewBox="0 0 12 12" fill="#e0a33c" style="vertical-align:-1px"><rect x="3" y="2.4" width="2.1" height="7.2" rx=".5"/><rect x="6.9" y="2.4" width="2.1" height="7.2" rx=".5"/></svg><svg width="11" height="11" viewBox="0 0 12 12" fill="#e0a33c" style="vertical-align:-1px"><path d="M3.4 2.4 9.4 6 3.4 9.6Z"/></svg> durma bitişi</span>
  <span><span class="swatch" style="background:transparent; border:1px dashed rgba(224,163,60,.55)"></span>durma — taralı bant + içindeki gün</span>
  <span><span class="swatch" style="background:#111; width:2px; height:13px; border-radius:0"></span>bugün</span>
`;

// Test/doğrulama için saf çekirdek fonksiyonları (React/DOM gerektirmez)
export { buildModel, buildTimelineHTML };

function ZamanAsimiTimeline({ timelineModel, sonuc }) {
  const [bilgiAcik, setBilgiAcik] = useState(false);

  if (!timelineModel || !timelineModel.runs || timelineModel.runs.length === 0) {
    return null;
  }

  const c = buildModel(timelineModel, sonuc);
  const { nleHTML, durSummaryHTML } = buildTimelineHTML(c);
  const doldu = timelineModel.zamanAsimiDoldu;
  const chipStyle = doldu
    ? { background: 'var(--red-bg)', color: 'var(--red)' }
    : { background: 'var(--green-bg)', color: 'var(--green)' };

  return (
    <div className="zsh">
      <div className="zsh-head">
        <span className="n">01</span>
        <h2>Zaman çizelgesi</h2>
        <button
          type="button"
          onClick={() => setBilgiAcik((v) => !v)}
          aria-expanded={bilgiAcik}
          title={bilgiAcik ? 'Açıklamayı gizle' : 'Renk felsefesi ve lejantı göster'}
          className={`inline-flex h-6 w-6 items-center justify-center self-center rounded-full border transition-colors ${
            bilgiAcik
              ? 'border-indigo-300 bg-indigo-50 text-indigo-600'
              : 'border-slate-300 text-slate-400 hover:bg-slate-100 hover:text-slate-600'
          }`}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
        <span className="chip" style={chipStyle}>
          {doldu ? 'Zamanaşımı doldu' : `Kalan: ${kalanSure(timelineModel).ifade}`}
        </span>
      </div>

      <div className="zsh-meta">
        <span>
          Suç tarihi <b className="mono">{dot(c.suc)}</b>
        </span>
        {sonuc?.yasGrubuBilgisi?.etiket ? (
          <span>
            Yaş <b>{sonuc.yasGrubuBilgisi.etiket}</b>
          </span>
        ) : null}
        {c.olaganLabel ? (
          <span>
            Uygulanan süre <b>{c.olaganLabel}</b>
          </span>
        ) : null}
        <span>
          Bugün <b className="mono">{dot(c.bugun)}</b>
        </span>
      </div>

      {bilgiAcik ? (
        <div className="zsh-bilgi mb-3 rounded-lg border border-slate-200 bg-slate-50/70 p-4">
          <p className="zsh-lead" style={{ margin: 0 }}>
            Renk felsefesi: <b>renk hangi zamanaşımı</b> olduğunu (olağan <b>mavi</b>, olağanüstü <b>indigo</b>),{' '}
            <b>ton</b> ne kadar geçerli olduğunu söyler. Çizelge iki alana bölünür: üstte <b>olağanüstü</b> (67/4 tavan,
            suç tarihinden işleyen tek dönem), altta <b>olağan</b> (son kesmeden işleyen süre koyu; kesmeyle sıfırlanan
            önceki dönem soluk). Her alanın altındaki <b>ince şeritler</b>, her olaydan ilgili süre dolana kadar
            uzanarak potansiyel zamanaşımı tarihlerini verir. Tüm tarihler en üstte harita-iğnesi rozetlerinde
            toplanır; eksen ölçekli değildir. <b>Önce dolan</b> tarih (kırmızı/yeşil) davanın düştüğü tarihtir.
          </p>
          <div
            className="nle-legend"
            style={{ marginTop: 12 }}
            dangerouslySetInnerHTML={{ __html: LEGEND_HTML }}
          />
        </div>
      ) : null}

      <div className="nle">
        <div className="nle-inner" dangerouslySetInnerHTML={{ __html: nleHTML }} />
      </div>
      <div className="zsh-scrollhint" aria-hidden="true">
        ← çizelgeyi yana kaydırın →
      </div>

      <div className="dur-summary" dangerouslySetInnerHTML={{ __html: durSummaryHTML }} />
    </div>
  );
}

export default ZamanAsimiTimeline;
